/**
 * Reader shortcuts.
 *
 * One listener on the window, on keydown, in the capture phase off. Two rules
 * decide everything: never steal a key from a field the user is typing in, and
 * never fight a browser shortcut, so anything with ctrl, meta or alt is left alone.
 */

import { useEffect, useRef } from "react";

import type { Key } from "../i18n";
import { loadAudioPrefs, subscribe } from "../lib/audioPrefs";

export interface KeyboardHandlers {
  toggle: () => void;
  nudge: (seconds: number) => void;
  step: (direction: 1 | -1) => void;
  rate: (direction: 1 | -1) => void;
  toggleView: () => void;
  toggleChapters: () => void;
  focusSearch: () => void;
  toggleHelp: () => void;
  escape: () => void;
}

export interface Shortcut {
  /** What is printed on the key. Never translated: it is engraved, not written. */
  keys: string;
  /** What the key does, as a catalogue key resolved at render time. */
  what: Key;
}

/**
 * The list the help dialog shows.
 *
 * `what` holds a catalogue key rather than a sentence, because this array is
 * built once when the module loads and would otherwise freeze whatever language
 * the interface happened to be in at that moment.
 */
export const SHORTCUTS: Shortcut[] = [
  { keys: "Space", what: "shortcutPlayPause" },
  { keys: "Left / Right", what: "shortcutNudge" },
  { keys: "Up / Down", what: "shortcutSentence" },
  { keys: "[ / ]", what: "shortcutRate" },
  { keys: "T", what: "shortcutView" },
  { keys: "C", what: "shortcutChapters" },
  { keys: "/", what: "chapterSearch" },
  { keys: "?", what: "shortcutHelp" },
  { keys: "Esc", what: "shortcutClose" },
];

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export function useKeyboard(handlers: KeyboardHandlers, enabled = true): void {
  // Kept in a ref so a new closure on every render does not resubscribe the listener.
  const latest = useRef(handlers);
  latest.current = handlers;

  // Same reasoning: read the configured skip lengths without resubscribing the
  // keydown listener every time the reader changes them in the settings menu.
  const skip = useRef(loadAudioPrefs());
  useEffect(() => subscribe((prefs) => {
    skip.current = prefs;
  }), []);

  useEffect(() => {
    if (!enabled) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      // Escape is the one key that has to work even from inside a field: it is how
      // a reader gets out of the search box.
      if (event.key === "Escape") {
        latest.current.escape();
        return;
      }
      if (isTyping(event.target)) return;

      const handle = (run: () => void) => {
        event.preventDefault();
        run();
      };

      switch (event.key) {
        case " ":
          handle(() => latest.current.toggle());
          break;
        case "ArrowLeft":
          handle(() => latest.current.nudge(-skip.current.skipBack));
          break;
        case "ArrowRight":
          handle(() => latest.current.nudge(skip.current.skipForward));
          break;
        case "ArrowUp":
          handle(() => latest.current.step(-1));
          break;
        case "ArrowDown":
          handle(() => latest.current.step(1));
          break;
        case "[":
          handle(() => latest.current.rate(-1));
          break;
        case "]":
          handle(() => latest.current.rate(1));
          break;
        case "t":
        case "T":
          handle(() => latest.current.toggleView());
          break;
        case "c":
        case "C":
          handle(() => latest.current.toggleChapters());
          break;
        case "/":
          handle(() => latest.current.focusSearch());
          break;
        case "?":
          handle(() => latest.current.toggleHelp());
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);
}
